/**
 * The two phone-only pieces of state the board needs (P-042 §3, M0-F9 §5.2 / F9-D28..D31).
 *
 * `usePhone()` is the same `(max-width: 767px)` the stylesheet uses, read through `matchMedia`
 * rather than a width literal so the component and `mobile.css` can never disagree about what a
 * phone is — `breakpoints.phone` is the one source of truth for both (M0-F9 §3).
 *
 * `usePhoneBoardLayout()` remembers whether this person reads boards as **cards** or as the
 * sticky-name **table**. monday has no mobile web and therefore no stored preference to copy;
 * the card asks for it "per user", and the honest per-user store the web app already uses for
 * exactly this class of state is `localStorage` (the same guarded pattern as `store.ts`'s
 * subitem expansion). It is deliberately **not** per board and not `view_user_state`: the
 * choice is about the device in the person's hand, not about one board's view, and a phone
 * that had to fetch it would render the wrong layout for one frame on every board.
 */
import { breakpoints } from '@elizmonday/ui';
import { useCallback, useEffect, useState } from 'react';

export type PhoneBoardLayout = 'cards' | 'table';

const PHONE_QUERY = `(max-width: ${breakpoints.phone}px)`;
const LAYOUT_KEY = 'ez.board.phone-layout';

/**
 * Round 9s. `phone.css` needs the phone condition **and** a `:has()` between two surfaces
 * neither of which owns the other (a toast versus an open editor sheet), and M0-F9 §3 allows no
 * second `(max-width: …)` outside `mobile.css`. So the fact `usePhone()` already computes from
 * `breakpoints.phone` is published on `<body>` for the stylesheet to key on — one source of
 * truth, stated twice in the two languages that need it.
 *
 * Ref-counted: `usePhone()` is mounted by the table view and the board header at the same time,
 * and the attribute belongs to the document, not to whichever of them unmounts first.
 */
const PHONE_ATTRIBUTE = 'ezPhone';
let phoneHolders = 0;

function holdPhoneAttribute(): () => void {
  const body = globalThis.document?.body;
  if (!body) return () => undefined;
  phoneHolders += 1;
  body.dataset[PHONE_ATTRIBUTE] = 'on';
  return () => {
    phoneHolders = Math.max(0, phoneHolders - 1);
    if (phoneHolders === 0) delete body.dataset[PHONE_ATTRIBUTE];
  };
}

/** `true` while the viewport is in the phone band. Server/JSDOM-safe: no `matchMedia` = false. */
export function usePhone(): boolean {
  const [phone, setPhone] = useState(() => {
    try {
      return globalThis.matchMedia?.(PHONE_QUERY).matches === true;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let list: MediaQueryList;
    try {
      list = globalThis.matchMedia(PHONE_QUERY);
    } catch {
      return;
    }
    const onChange = () => setPhone(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!phone) return;
    return holdPhoneAttribute();
  }, [phone]);

  return phone;
}

function readLayout(): PhoneBoardLayout {
  try {
    return globalThis.localStorage?.getItem(LAYOUT_KEY) === 'table' ? 'table' : 'cards';
  } catch {
    /* a private window or blocked storage costs the preference, never the board */
    return 'cards';
  }
}

/**
 * `['cards' | 'table', set]`. The default is **cards**: a phone that shows a 594 px name column
 * and one truncated data cell is the defect P-042 reports, so the drastic reading has to be what
 * a person gets without choosing anything.
 */
export function usePhoneBoardLayout(): [PhoneBoardLayout, (next: PhoneBoardLayout) => void] {
  const [layout, setLayout] = useState<PhoneBoardLayout>(readLayout);

  const set = useCallback((next: PhoneBoardLayout) => {
    setLayout(next);
    try {
      globalThis.localStorage?.setItem(LAYOUT_KEY, next);
    } catch {
      /* the choice still holds for this session */
    }
  }, []);

  return [layout, set];
}
